import { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db, addAuditLog } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import styles from './OrdersPage.module.css';
import { formatOrderId } from '../../utils/formatOrderId';

const STATUS_FILTERS = ['All', 'Pending', 'Confirmed', 'In Transit', 'Delivered', 'Rejected'];
const STATUS_OPTIONS  = ['Pending', 'Confirmed', 'In Transit', 'Delivered', 'Rejected'];
const normalizeStatus = s => s === 'On its way' ? 'In Transit' : s;
const PAGE_SIZE = 5;

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 10);
}

function formatTotal(total) {
  if (typeof total === 'number') return `₱${total.toLocaleString()}`;
  return total ?? '—';
}

function StatusBadge({ status }) {
  return <span className={`${styles.badge} ${styles[`badge_${status.replace(/\s+/g, '_')}`]}`}>{status}</span>;
}

export default function OrdersPage() {
  const { user }                      = useAuth();
  const [orders, setOrders]           = useState([]);
  const [search, setSearch]           = useState('');
  const [activeFilter, setFilter]     = useState('All');
  const [editingId, setEditingId]     = useState(null);
  const [editStatus, setEditStatus]   = useState('');
  const [editItems, setEditItems]     = useState([]);
  const [page, setPage]               = useState(1);
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setOrders(snap.docs.map(d => {
        const data = d.data();
        return { _docId: d.id, ...data, status: normalizeStatus(data.status) };
      }));
    });
    return unsub;
  }, []);

  const filtered = orders
    .filter(o => {
      const matchesSearch = !search ||
        (o._docId || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.branch  || '').toLowerCase().includes(search.toLowerCase());
      const matchesFilter = activeFilter === 'All' || o.status === activeFilter;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      const aPending = a.status === 'Pending';
      const bPending = b.status === 'Pending';
      if (aPending && bPending) return (b.wfqScore ?? 0) - (a.wfqScore ?? 0);
      if (aPending) return -1;
      if (bPending) return 1;
      const aTime = a.createdAt?.toDate?.() ?? new Date(0);
      const bTime = b.createdAt?.toDate?.() ?? new Date(0);
      return bTime - aTime;
    });

  const pendingRanks = filtered.reduce((acc, o, i) => {
    if (o.status === 'Pending') acc[o._docId] = Object.keys(acc).length + 1;
    return acc;
  }, {});

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function openEdit(order) {
    setEditingId(order._docId);
    setEditStatus(order.status);
    setEditItems((order.items || []).map(i => ({ ...i })));
  }

  async function saveEdit() {
    if (saving) return;
    setSaving(true);
    const newTotal = editItems.reduce((s, i) => s + (i.qty * (i.price ?? 0)), 0);
    try {
      await updateDoc(doc(db, 'orders', editingId), {
        status:    editStatus,
        items:     editItems,
        ...(editItems.length > 0 ? { total: newTotal } : {}),
        updatedAt: serverTimestamp(),
      });
      await addAuditLog(user.uid, user.name, `Updated order ${editingId} — status: "${editStatus}"`, 'Orders');
      setEditingId(null);
    } catch (e) {
      console.error('Update order failed:', e);
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove(order) {
    try {
      await updateDoc(doc(db, 'orders', order._docId), { status: 'In Transit', updatedAt: serverTimestamp() });
      await addAuditLog(user.uid, user.name, `Approved order ${order._docId}`, 'Orders');
    } catch (e) { console.error('Approve failed:', e); }
  }

  async function handleReject(order) {
    try {
      await updateDoc(doc(db, 'orders', order._docId), { status: 'Rejected', updatedAt: serverTimestamp() });
      await addAuditLog(user.uid, user.name, `Rejected order ${order._docId}`, 'Orders');
    } catch (e) { console.error('Reject failed:', e); }
  }

  return (
    <div className={styles.page}>
      <PageHeader title="Order Management" description="Review and process branch supply orders" />

      <div className={styles.searchWrap}>
        <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8"/>
          <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search by Order ID or Branch…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      <div className={styles.filterRow}>
        {STATUS_FILTERS.map(f => (
          <button key={f} className={`${styles.pill} ${activeFilter === f ? styles.pillActive : ''}`} onClick={() => { setFilter(f); setPage(1); }}>
            {f}
          </button>
        ))}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Priority</th><th>Order ID</th><th>Branch</th><th>Order Details</th>
              <th>Total</th><th>Date</th><th>Status</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={7} className={styles.empty}>No orders match your search.</td></tr>
            ) : (
              pageRows.map((order, idx) => (
                <tr key={order._docId} className={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td className={styles.priorityCell}>
                    {order.status === 'Pending' && pendingRanks[order._docId] != null ? (
                      <span className={`${styles.priorityBadge} ${pendingRanks[order._docId] === 1 ? styles.priorityTop : ''}`}>
                        #{pendingRanks[order._docId]}
                      </span>
                    ) : '—'}
                  </td>
                  <td className={styles.orderId}>{formatOrderId(order._docId)}</td>
                  <td>{order.branch}</td>
                  <td className={styles.details}>{order.details || (order.items || []).map(i => `${i.productId} (${i.qty}x)`).join(', ')}</td>
                  <td className={styles.total}>{formatTotal(order.total)}</td>
                  <td>{formatDate(order.createdAt)}</td>
                  <td><StatusBadge status={order.status} /></td>
                  <td>
                    <div className={styles.actionCell}>
                      {order.status === 'Pending' && (
                        <>
                          <button className={styles.approveBtn} onClick={() => handleApprove(order)}>Approve</button>
                          <button className={styles.rejectBtn}  onClick={() => handleReject(order)}>Reject</button>
                        </>
                      )}
                      <button className={styles.editBtn} onClick={() => openEdit(order)}>Edit</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <button className={styles.pageBtn} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>← Previous</button>
        <span className={styles.pageIndicator}>Page {safePage} of {totalPages}</span>
        <button className={styles.pageBtn} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>Next →</button>
      </div>

      {editingId && (
        <div className={styles.overlay} onClick={() => setEditingId(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Edit Order — {editingId}</h3>
            <label className={styles.modalLabel}>
              Status
              <select className={styles.modalSelect} value={editStatus} onChange={e => setEditStatus(e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            {editItems.length > 0 && (
              <div className={styles.qtySection}>
                <span className={styles.qtyTitle}>Adjust Item Quantities</span>
                {editItems.map((item, i) => (
                  <div key={i} className={styles.qtyRow}>
                    <span className={styles.qtyProduct}>{item.productId}</span>
                    <input
                      type="number"
                      className={styles.qtyInput}
                      min={1}
                      value={item.qty}
                      onChange={e => {
                        const v = Math.max(1, Number(e.target.value));
                        setEditItems(prev => prev.map((it, idx) => idx === i ? { ...it, qty: v } : it));
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setEditingId(null)}>Cancel</button>
              <button className={styles.saveBtn} onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
